import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Card, Table, Spinner, Alert, Button, Badge, Row, Col, Modal } from 'react-bootstrap';
import api, { formatApiErrorDetail, getPublicUploadsOrigin } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';

const ResultadosConvocatoria = () => {
  const { nroConvocatoria } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [convocatoria, setConvocatoria] = useState(null);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCotizacion, setSelectedCotizacion] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
      return;
    }
    loadData();
  }, [nroConvocatoria, isAuthenticated]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Cargar información de la convocatoria
      const convResponse = await api.get(`/api/v1/convocatorias/public/${nroConvocatoria}`);
      setConvocatoria(convResponse.data);

      // Cargar todas las cotizaciones de la convocatoria
      const cotResponse = await api.get(`/api/v1/cotizaciones/convocatoria/${nroConvocatoria}`);
      setCotizaciones(cotResponse.data || []);
    } catch (e) {
      console.error('Error loading data:', e);
      const backendMessage = formatApiErrorDetail(e, 'Error al cargar los resultados');
      setError(backendMessage);
    } finally {
      setLoading(false);
    }
  };

  const sanitizeFragment = (value) => {
    if (!value) return '';
    const trimmed = String(value).trim().replace(/\s+/g, '_');
    return trimmed.replace(/[^a-zA-Z0-9_-]/g, '');
  };

  const evaluacionFileUrl = (cotizacion) => {
    if (!cotizacion?.evaluacion_ficha_calificacion_pdf) return null;
    const codCotizacion = cotizacion.cod_cotizacion || cotizacion.id_cotizacion;
    const folder = sanitizeFragment(codCotizacion);
    return `${getPublicUploadsOrigin()}/uploads/evaluaciones/${folder}/${cotizacion.evaluacion_ficha_calificacion_pdf}`;
  };

  const tieneEvaluacion = (c) => {
    return !!(c.evaluacion_marca || c.evaluacion_precio_ofertado || c.evaluacion_plazo_entrega || 
              c.evaluacion_validez_cotizacion || c.evaluacion_observaciones || c.evaluacion_ficha_calificacion_pdf ||
              c.evaluacion_seleccionado !== undefined || c.evaluacion_fecha_registro);
  };

  const openDetalle = (cotizacion) => {
    setSelectedCotizacion(cotizacion);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      {/* Header con fondo y logo */}
      <div style={{ 
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)), url(/fondo.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '2rem 0',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center">
                <img 
                  src="/LOGO-DIRESA.png" 
                  alt="Logo DIRESA" 
                  className="me-3"
                  style={{ 
                    height: '90px', 
                    objectFit: 'contain',
                    filter: 'brightness(0) invert(1)'
                  }}
                />
                <div>
                  <h1 className="text-white mb-1 fw-bold" style={{ fontSize: '1.75rem' }}>
                    Resultados de Convocatoria
                  </h1>
                  <p className="text-white-50 mb-0 small">DIRESA - Portal Público</p>
                </div>
              </div>
            </Col>
            <Col md={6} className="text-end">
              <Button as={Link} to="/mis-cotizaciones" variant="light" className="px-4">
                <i className="bi bi-arrow-left me-2"></i>
                Volver a Mis Postulaciones
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        {error && (
          <Alert variant="danger" className="mb-3">{error}</Alert>
        )}

        {/* Información de la Convocatoria */}
        {convocatoria && (
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Body>
              <h3 className="mb-3">Convocatoria: {convocatoria.COD_CONVOCATORIA || `#${nroConvocatoria}`}</h3>
              <Row>
                <Col md={6}>
                  <div className="mb-2">
                    <strong>Rubro:</strong> {convocatoria.rubro || 'N/A'}
                  </div>
                  <div className="mb-2">
                    <strong>Concepto:</strong> {convocatoria.concepto || 'N/A'}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <strong>Estado:</strong>{' '}
                    <Badge bg={
                      convocatoria.estado === 'ABIERTA' ? 'success' :
                      convocatoria.estado === 'CERRADA' ? 'secondary' :
                      convocatoria.estado === 'EVALUACION' ? 'warning' :
                      convocatoria.estado === 'FINALIZADA' ? 'info' : 'secondary'
                    }>
                      {convocatoria.estado || 'N/A'}
                    </Badge>
                  </div>
                  <div className="mb-2">
                    <strong>Nro. Pedido:</strong> {convocatoria.nro_pedido || 'N/A'}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Tabla de Proveedores */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="p-3 border-bottom">
              <h5 className="mb-0">
                Proveedores que Postularon ({cotizaciones.length})
              </h5>
            </div>
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead style={{ backgroundColor: '#2b3e53', color: 'white' }}>
                  <tr>
                    <th className="border-0 py-3 px-3">#</th>
                    <th className="border-0 py-3 px-3">Proveedor</th>
                    <th className="border-0 py-3 px-3">RUC</th>
                    <th className="border-0 py-3 px-3">Cód. Cotización</th>
                    <th className="border-0 py-3 px-3">Marca</th>
                    <th className="border-0 py-3 px-3">Precio Ofertado</th>
                    <th className="border-0 py-3 px-3">Plazo Entrega</th>
                    <th className="border-0 py-3 px-3">Observaciones</th>
                    <th className="border-0 py-3 px-3">Estado</th>
                    <th className="border-0 py-3 px-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cotizaciones.length === 0 ? (
                    <tr>
                      <td className="py-4 px-3 text-center" colSpan={10}>
                        No hay proveedores que hayan postulado para esta convocatoria.
                      </td>
                    </tr>
                  ) : (
                    cotizaciones.map((cot, index) => {
                      const esGanador = cot.evaluacion_seleccionado === true;
                      const evaluada = tieneEvaluacion(cot);
                      return (
                        <tr 
                          key={cot.id_cotizacion}
                          style={{
                            backgroundColor: esGanador ? '#fff3cd' : evaluada ? '#f0f8f0' : 'white',
                            borderLeft: esGanador ? '4px solid #ffc107' : evaluada ? '4px solid #28a745' : '4px solid transparent'
                          }}
                        >
                          <td className="py-3 px-3">{index + 1}</td>
                          <td className="py-3 px-3">
                            <div className="d-flex align-items-center gap-2">
                              {esGanador && (
                                <Badge bg="warning" text="dark">
                                  <i className="bi bi-trophy-fill"></i>
                                </Badge>
                              )}
                              {evaluada && !esGanador && (
                                <Badge bg="success">
                                  <i className="bi bi-check-circle"></i>
                                </Badge>
                              )}
                              {!evaluada && (
                                <Badge bg="secondary">
                                  <i className="bi bi-clock"></i>
                                </Badge>
                              )}
                              <span>{cot.proveedor_razon_social || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-3">{cot.proveedor_ruc || 'N/A'}</td>
                          <td className="py-3 px-3"><code>{cot.cod_cotizacion || 'N/A'}</code></td>
                          <td className="py-3 px-3">
                            {cot.evaluacion_marca || <span className="text-muted">-</span>}
                          </td>
                          <td className="py-3 px-3">
                            {cot.evaluacion_precio_ofertado 
                              ? `S/ ${parseFloat(cot.evaluacion_precio_ofertado).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : <span className="text-muted">-</span>}
                          </td>
                          <td className="py-3 px-3">
                            {cot.evaluacion_plazo_entrega 
                              ? `${cot.evaluacion_plazo_entrega} días`
                              : <span className="text-muted">-</span>}
                          </td>
                          <td className="py-3 px-3" style={{ maxWidth: '250px' }}>
                            {cot.evaluacion_observaciones ? (
                              <div style={{ maxHeight: '60px', overflow: 'auto' }}>
                                <small className="text-muted" title={cot.evaluacion_observaciones}>
                                  {cot.evaluacion_observaciones.length > 80 
                                    ? cot.evaluacion_observaciones.substring(0, 80) + '...'
                                    : cot.evaluacion_observaciones}
                                </small>
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            {esGanador ? (
                              <Badge bg="warning" text="dark">
                                <i className="bi bi-trophy-fill me-1"></i>
                                GANADOR
                              </Badge>
                            ) : evaluada ? (
                              <Badge bg="success">
                                <i className="bi bi-check-circle me-1"></i>
                                Evaluada
                              </Badge>
                            ) : (
                              <Badge bg="secondary">
                                <i className="bi bi-clock me-1"></i>
                                Pendiente
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            {evaluada && (
                              <Button 
                                onClick={() => openDetalle(cot)} 
                                size="sm" 
                                variant="outline-primary"
                              >
                                <i className="bi bi-eye me-1"></i>
                                Ver Detalles
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Container>

      <Footer />

      {/* Modal de Detalles de Evaluación */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-clipboard-check me-2"></i>
            Detalles de Evaluación
            {selectedCotizacion?.evaluacion_seleccionado && (
              <Badge bg="warning" text="dark" className="ms-2">
                <i className="bi bi-trophy-fill me-1"></i>
                GANADOR
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCotizacion && (
            <>
              <div className="mb-4">
                <h6 className="fw-bold mb-3">Información del Proveedor</h6>
                <Row className="g-3">
                  <Col md={6}>
                    <div>
                      <small className="text-muted">Razón Social:</small>
                      <div><strong>{selectedCotizacion.proveedor_razon_social || 'N/A'}</strong></div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div>
                      <small className="text-muted">RUC:</small>
                      <div><strong>{selectedCotizacion.proveedor_ruc || 'N/A'}</strong></div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div>
                      <small className="text-muted">Código de Cotización:</small>
                      <div><code>{selectedCotizacion.cod_cotizacion || 'N/A'}</code></div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="fw-bold mb-3">Criterios de Evaluación</h6>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Marca</small>
                      <strong>{selectedCotizacion.evaluacion_marca || 'No especificado'}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Precio Ofertado</small>
                      <strong>
                        {selectedCotizacion.evaluacion_precio_ofertado 
                          ? `S/ ${parseFloat(selectedCotizacion.evaluacion_precio_ofertado).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : 'No especificado'}
                      </strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Plazo de Entrega</small>
                      <strong>
                        {selectedCotizacion.evaluacion_plazo_entrega 
                          ? `${selectedCotizacion.evaluacion_plazo_entrega} días`
                          : 'No especificado'}
                      </strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Validez de Cotización</small>
                      <strong>
                        {selectedCotizacion.evaluacion_validez_cotizacion 
                          ? `${selectedCotizacion.evaluacion_validez_cotizacion} días`
                          : 'No especificado'}
                      </strong>
                    </div>
                  </Col>
                </Row>
              </div>

              {selectedCotizacion.evaluacion_observaciones && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-2">Observaciones</h6>
                  <div className="p-3 bg-light rounded">
                    {selectedCotizacion.evaluacion_observaciones}
                  </div>
                </div>
              )}

              {selectedCotizacion.evaluacion_ficha_calificacion_pdf && (
                <div className="mb-3">
                  <h6 className="fw-bold mb-2">Ficha de Calificación</h6>
                  <div>
                    <a 
                      href={evaluacionFileUrl(selectedCotizacion)} 
                      target="_blank" 
                      rel="noreferrer"
                      className="btn btn-outline-primary btn-sm"
                    >
                      <i className="bi bi-file-pdf me-1"></i>
                      Ver Ficha de Calificación (PDF)
                    </a>
                  </div>
                </div>
              )}

              {selectedCotizacion.evaluacion_fecha_registro && (
                <div className="text-muted small">
                  <i className="bi bi-calendar me-1"></i>
                  Fecha de evaluación: {new Date(selectedCotizacion.evaluacion_fecha_registro).toLocaleString('es-PE')}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ResultadosConvocatoria;

